import React, { useEffect, useState } from 'react';
import TopNav from '../TopNav';
import api, { deleteUserByAdmin } from '../../services/api';

interface User {
  id: string;
  name: string;
  email: string;
  role?: 'USER' | 'ADMIN';
  billingPlan: 'FREE' | 'PRO';
  billingStatus: 'NONE' | 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'WAIVED_TEMP' | 'WAIVED_INFINITE';
  trialEnd: string | null;
  nextBillingDate: string | null;
  subscriptionStatus: string | null; // Raw Stripe subscription status
  waiverType: 'NONE' | 'ONE_MONTH' | 'NINETY_DAYS' | 'INFINITE';
  waiverEnd: string | null;
  createdAt: string;
  accounts: Array<{
    id: string;
    status: string;
    environment: string;
  }>;
}

/**
 * Users Management Component
 * Admin-only component to view and manage users with billing controls
 */
const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [waiverReason, setWaiverReason] = useState('');
  const [processingWaiver, setProcessingWaiver] = useState(false);
  const [waiverSuccess, setWaiverSuccess] = useState<string | null>(null);
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState<string | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState<string>('');
  const [deleteConfirmEmailInput, setDeleteConfirmEmailInput] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/admin/users');
      const userData = response.data?.data?.users || [];
      setUsers(userData);
    } catch (e: any) {
      console.error('Failed to fetch users:', e);
      setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantWaiver = async (userId: string, type: 'ONE_MONTH' | 'NINETY_DAYS' | 'INFINITE' | 'NONE') => {
    try {
      setProcessingWaiver(true);
      setError(null);
      setWaiverSuccess(null);

      // Map frontend type to backend expected format
      const typeMap: Record<string, string> = {
        'ONE_MONTH': '1_month',
        'NINETY_DAYS': '90_days',
        'INFINITE': 'infinite',
        'NONE': 'none',
      };

      const backendType = typeMap[type] || 'none';

      const response = await api.post(`/admin/users/${userId}/waiver`, {
        type: backendType,
        reason: waiverReason || `Admin granted ${type.replace('_', ' ').toLowerCase()} waiver`,
      });

      if (response.data?.success) {
        setWaiverSuccess(`Waiver ${type === 'NONE' ? 'cleared' : 'granted'} successfully`);
        setShowWaiverModal(false);
        setSelectedUser(null);
        setWaiverReason('');
        // Refresh users list
        await fetchUsers();
        // Clear success message after 3 seconds
        setTimeout(() => setWaiverSuccess(null), 3000);
      } else {
        setError(response.data?.error || 'Failed to grant waiver');
      }
    } catch (e: any) {
      console.error('Failed to grant waiver:', e);
      setError(e?.response?.data?.error || e?.message || 'Failed to grant waiver');
    } finally {
      setProcessingWaiver(false);
    }
  };

  const openWaiverModal = (userId: string) => {
    setSelectedUser(userId);
    setShowWaiverModal(true);
    setWaiverReason('');
  };
  
  // Filter users based on search and status
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'ALL' || 
      user.billingStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  // Calculate days remaining for temporary waivers
  const getDaysRemaining = (endDate: string | null): number | null => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
          <p className="text-neutral-400">View and manage system users</p>
        </div>

        {error && (
          <div className="card bg-red-900/20 border border-red-500 p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {waiverSuccess && (
          <div className="card bg-green-900/20 border border-green-500 p-4 mb-6">
            <p className="text-green-400">{waiverSuccess}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
            <p className="text-neutral-400">Loading users...</p>
          </div>
        ) : (
          <div className="card bg-neutral-900 p-6">
            {/* Search and Filter Controls */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 bg-neutral-800 border border-neutral-700 rounded text-white placeholder-neutral-500 focus:outline-none focus:border-cyan-500"
                />
              </div>
              <div className="sm:w-64">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-neutral-800 border border-neutral-700 rounded text-white focus:outline-none focus:border-cyan-500"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="TRIALING">🎁 Trialing</option>
                  <option value="ACTIVE">✅ Active</option>
                  <option value="WAIVED_INFINITE">♾️ Waived (Infinite)</option>
                  <option value="WAIVED_TEMP">⏱️ Waived (Temporary)</option>
                  <option value="PAST_DUE">⚠️ Past Due</option>
                  <option value="CANCELED">❌ Canceled</option>
                  <option value="NONE">🆓 Free (Never Subscribed)</option>
                </select>
              </div>
            </div>
            
            {/* Results count */}
            <div className="mb-4 text-sm text-neutral-400">
              Showing {filteredUsers.length} of {users.length} users
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-neutral-700">
                    <th className="pb-3 text-neutral-300 font-medium">User</th>
                    <th className="pb-3 text-neutral-300 font-medium">Email</th>
                    <th className="pb-3 text-neutral-300 font-medium">Role</th>
                    <th className="pb-3 text-neutral-300 font-medium">Billing Status</th>
                    <th className="pb-3 text-neutral-300 font-medium">Waiver</th>
                    <th className="pb-3 text-neutral-300 font-medium">Accounts</th>
                    <th className="pb-3 text-neutral-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-neutral-800">
                      <td className="py-4">
                        <div className="text-white font-medium">
                          {user.name}
                        </div>
                      </td>
                      <td className="py-4 text-neutral-300">{user.email}</td>
                      <td className="py-4">
                        {user.role && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.role === 'ADMIN' 
                              ? 'bg-purple-900/30 text-purple-300 border border-purple-700' 
                              : 'bg-blue-900/30 text-blue-300 border border-blue-700'
                          }`}>
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td className="py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${
                              user.billingStatus === 'ACTIVE'
                                ? 'bg-green-900/30 text-green-300 border border-green-700'
                                : user.billingStatus === 'TRIALING'
                                ? 'bg-blue-900/30 text-blue-300 border border-blue-700'
                                : user.billingStatus === 'WAIVED_INFINITE' || user.billingStatus === 'WAIVED_TEMP'
                                ? 'bg-cyan-900/30 text-cyan-300 border border-cyan-700'
                                : user.billingStatus === 'CANCELED' || user.billingStatus === 'PAST_DUE'
                                ? 'bg-red-900/30 text-red-300 border border-red-700'
                                : 'bg-neutral-800/50 text-neutral-400 border border-neutral-700'
                            }`}>
                              {user.billingStatus === 'TRIALING' && '🎁 '}
                              {user.billingStatus === 'ACTIVE' && '✅ '}
                              {user.billingStatus === 'WAIVED_INFINITE' && '♾️ '}
                              {user.billingStatus === 'WAIVED_TEMP' && '⏱️ '}
                              {user.billingStatus.replace('_', ' ')}
                            </span>
                            <span className="text-xs text-neutral-500">
                              {user.billingPlan}
                            </span>
                          </div>
                          
                          {/* Trial Information */}
                          {(user.billingStatus === 'TRIALING' || user.subscriptionStatus === 'trialing') && (
                            <div className="flex flex-col gap-1 p-2 bg-blue-900/20 rounded border border-blue-700/50 mt-1">
                              <span className="text-xs text-blue-300 font-medium">
                                🎁 7-Day Free Trial Active
                              </span>
                              {user.trialEnd && (
                                <span className="text-xs text-neutral-400">
                                  Ends: {new Date(user.trialEnd).toLocaleDateString()}
                                </span>
                              )}
                              {user.waiverType !== 'NONE' && (
                                <span className="text-xs text-orange-400">
                                  ⚠️ Waiver will activate after trial
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Active Subscription Info */}
                          {user.billingStatus === 'ACTIVE' && user.nextBillingDate && (
                            <span className="text-xs text-neutral-500">
                              Next billing: {new Date(user.nextBillingDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4">
                        {user.waiverType !== 'NONE' ? (
                          <div className="flex flex-col gap-1 p-2 bg-cyan-900/20 rounded border border-cyan-700">
                            <div className="flex items-center gap-2">
                              <span className="text-cyan-300 font-medium text-sm">
                                {user.waiverType === 'INFINITE' && '♾️ '}
                                {user.waiverType === 'ONE_MONTH' && '📅 '}
                                {user.waiverType === 'NINETY_DAYS' && '📆 '}
                                {user.waiverType.replace('_', ' ')}
                              </span>
                            </div>
                            {user.waiverEnd ? (
                              <>
                                <span className="text-xs text-neutral-400">
                                  Expires: {new Date(user.waiverEnd).toLocaleDateString()}
                                </span>
                                {(() => {
                                  const daysRemaining = getDaysRemaining(user.waiverEnd);
                                  if (daysRemaining !== null && daysRemaining > 0) {
                                    return (
                                      <span className={`text-xs font-medium ${
                                        daysRemaining <= 3 ? 'text-orange-400' : 'text-cyan-300'
                                      }`}>
                                        ⏱️ {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            ) : (
                              <span className="text-xs text-green-400 font-medium">
                                ♾️ Never Expires
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-neutral-500">No waiver</span>
                        )}
                      </td>
                      <td className="py-4 text-neutral-300">
                        {user.accounts?.length || 0} account{user.accounts?.length !== 1 ? 's' : ''}
                      </td>
                      <td className="py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openWaiverModal(user.id)}
                            className="px-3 py-1 text-xs bg-cyan-600 hover:bg-cyan-700 text-white rounded transition-colors"
                          >
                            Manage Waiver
                          </button>
                          {user.role !== 'ADMIN' && (
                            <button
                              onClick={() => {
                                setDeleteConfirmUserId(user.id);
                                setDeleteConfirmEmail(user.email);
                                setDeleteConfirmEmailInput('');
                              }}
                              className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredUsers.length === 0 && users.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-neutral-400">No users match your search criteria</p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('ALL');
                    }}
                    className="mt-4 px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
              
              {users.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-neutral-400">No users found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Waiver Management Modal */}
        {showWaiverModal && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70">
            <div className="bg-neutral-800 p-6 rounded-lg shadow-xl w-full max-w-md border border-neutral-700">
              <h3 className="text-xl font-bold text-white mb-2">Manage Fee Waiver</h3>
              <p className="text-sm text-neutral-400 mb-1">
                Grant free access to this user or resume billing.
              </p>
              <div className="bg-blue-900/20 border border-blue-700/50 rounded p-3 mb-4">
                <p className="text-xs text-blue-300">
                  💡 <strong>How waivers work:</strong>
                </p>
                <ul className="text-xs text-neutral-400 mt-2 space-y-1 ml-4 list-disc">
                  <li>Waivers grant free access without subscription</li>
                  <li>If user is in trial, waiver activates after trial ends</li>
                  <li>If user has active subscription, it will be paused</li>
                  <li>Temporary waivers auto-expire and resume billing</li>
                  <li>Infinite waivers never expire (manual clear only)</li>
                </ul>
              </div>
              
              <div className="space-y-3 mb-4">
                <button
                  onClick={() => handleGrantWaiver(selectedUser, 'ONE_MONTH')}
                  disabled={processingWaiver}
                  className="w-full px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded transition-colors disabled:opacity-50 text-left"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">📅 1 Month Free</span>
                    <span className="text-xs opacity-75">30 days</span>
                  </div>
                  <p className="text-xs opacity-75 mt-1">Grants access for 30 days, then auto-resumes billing</p>
                </button>
                <button
                  onClick={() => handleGrantWaiver(selectedUser, 'NINETY_DAYS')}
                  disabled={processingWaiver}
                  className="w-full px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded transition-colors disabled:opacity-50 text-left"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">📆 90 Days Free</span>
                    <span className="text-xs opacity-75">3 months</span>
                  </div>
                  <p className="text-xs opacity-75 mt-1">Grants access for 90 days, then auto-resumes billing</p>
                </button>
                <button
                  onClick={() => handleGrantWaiver(selectedUser, 'INFINITE')}
                  disabled={processingWaiver}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50 text-left"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">♾️ Infinite Free</span>
                    <span className="text-xs opacity-75">Forever</span>
                  </div>
                  <p className="text-xs opacity-75 mt-1">Permanent free access (must clear manually)</p>
                </button>
                <button
                  onClick={() => handleGrantWaiver(selectedUser, 'NONE')}
                  disabled={processingWaiver}
                  className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50 text-left"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">❌ Clear Waiver</span>
                    <span className="text-xs opacity-75">Resume billing</span>
                  </div>
                  <p className="text-xs opacity-75 mt-1">Removes waiver and resumes normal subscription billing</p>
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={waiverReason}
                  onChange={(e) => setWaiverReason(e.target.value)}
                  placeholder="Enter reason for waiver..."
                  className="w-full px-3 py-2 bg-neutral-700 text-white rounded border border-neutral-600 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowWaiverModal(false);
                    setSelectedUser(null);
                    setWaiverReason('');
                  }}
                  disabled={processingWaiver}
                  className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>

              {processingWaiver && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-400 mx-auto"></div>
                  <p className="text-sm text-neutral-400 mt-2">Processing...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {deleteConfirmUserId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70">
            <div className="bg-neutral-800 p-6 rounded-lg shadow-xl w-full max-w-md border border-neutral-700">
              <h3 className="text-xl font-bold text-white mb-4">Delete User Account</h3>
              
              <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4">
                <p className="text-red-300 text-sm mb-2">
                  <strong>Warning:</strong> This action cannot be undone.
                </p>
                <p className="text-red-300 text-sm">
                  All user data including trades, accounts, preferences, and subscription information will be permanently deleted.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Type <strong>{deleteConfirmEmail}</strong> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmEmailInput}
                  onChange={(e) => setDeleteConfirmEmailInput(e.target.value)}
                  placeholder={deleteConfirmEmail}
                  className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm mb-4">{error}</p>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteConfirmUserId(null);
                    setDeleteConfirmEmail('');
                    setDeleteConfirmEmailInput('');
                    setError(null);
                  }}
                  disabled={deleting}
                  className="px-4 py-2 border border-neutral-600 hover:border-neutral-500 text-neutral-300 hover:text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (deleteConfirmEmailInput !== deleteConfirmEmail) {
                      setError('Email does not match');
                      return;
                    }

                    setDeleting(true);
                    setError(null);

                    try {
                      const response = await deleteUserByAdmin(deleteConfirmUserId);

                      if (response.success) {
                        setDeleteConfirmUserId(null);
                        setDeleteConfirmEmail('');
                        setDeleteConfirmEmailInput('');
                        await fetchUsers();
                        setWaiverSuccess('User deleted successfully');
                        setTimeout(() => setWaiverSuccess(null), 3000);
                      } else {
                        setError(typeof response.error === 'string' ? response.error : 'Failed to delete user');
                      }
                    } catch (err: any) {
                      console.error('Error deleting user:', err);
                      setError(err?.response?.data?.error || err?.message || 'Failed to delete user');
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  disabled={deleting || deleteConfirmEmailInput !== deleteConfirmEmail}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-neutral-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  {deleting ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
